/**
 * Hotel List Component
 */

import React, { useState, useMemo, useContext } from 'react';
import {
  EditButton,
  List,
  NumberField,
  TextField,
  useTranslate,
} from 'react-admin';
import { Box, Button, useTheme } from '@mui/material';
import { FilterList as FilterListIcon, Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { CustomDatagrid } from '@/components/common';
import { compactListSx } from '@/components/common/compactListSx';
import { actionButtonSx, filterToggleSx } from '@/components/common/ListPageHeader';
import { CollapsibleFilterPanel, FilterExpandContext } from '@/components/common/CollapsibleFilterPanel';
import { CustomTextInputFilter } from '@/components/common/CustomFilters';

const HotelActions = () => {
  const filterExpand = useContext(FilterExpandContext);
  const translate = useTranslate();
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Button
        onClick={() => filterExpand.setExpanded(!filterExpand.expanded)}
        size="small"
        variant={filterExpand.expanded ? 'contained' : 'outlined'}
        startIcon={<FilterListIcon sx={{ fontSize: '16px !important' }} />}
        sx={filterToggleSx(theme, filterExpand.expanded)}
      >
        {translate('common.filters.label')}
      </Button>
      <Button
        onClick={() => navigate('/hotels/create')}
        size="small"
        variant="contained"
        startIcon={<AddIcon />}
        sx={actionButtonSx}
      >
        {translate('common.create')}
      </Button>
    </Box>
  );
};

export const HotelList = () => {
  const translate = useTranslate();
  const f = (key: string) => translate(`resources.hotels.fields.${key}`);

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const filterExpandValue = useMemo(() => ({
    expanded: filtersExpanded,
    setExpanded: setFiltersExpanded,
  }), [filtersExpanded]);

  const filters = useMemo(() => [
    <CollapsibleFilterPanel
      key="filters"
      source="_cfp"
      alwaysOn
      alwaysVisibleCount={0}
      defaultExpanded
    >
      <CustomTextInputFilter source="q" label={translate('common.search')} alwaysOn />
    </CollapsibleFilterPanel>,
  ], [translate]);

  return (
    <FilterExpandContext.Provider value={filterExpandValue}>
      <List sx={compactListSx} filters={filters} actions={<HotelActions />} perPage={25}>
        <CustomDatagrid primarySource="name" secondarySource="city" detailColumns={2}>
          <TextField source="name" label={f('name')} />
          <TextField source="hotelChainName" label={f('hotelChainName')} sortable={false} />
          <TextField source="hotelCategoryName" label={f('hotelCategoryName')} sortable={false} />
          <TextField source="city" label={f('city')} />
          <TextField source="country" label={f('country')} />
          <TextField source="pmsSystemName" label={f('pmsSystemName')} sortable={false} />
          <TextField source="lockSystemName" label={f('lockSystemName')} sortable={false} />
          <TextField source="paymentProcessorName" label={f('paymentProcessorName')} sortable={false} />
          <NumberField source="leadCount" label={f('leadCount')} sortable={false} />
          <EditButton />
        </CustomDatagrid>
      </List>
    </FilterExpandContext.Provider>
  );
};

export default HotelList;
